/**
 * SubscriptionPage - Manage subscription
 *
 * Simple page for users to:
 * - View current subscription
 * - Cancel subscription
 * - Reactivate subscription
 * - Change plan (monthly ↔ annual)
 * - Start subscription for DEFERRED_TRIAL users
 *
 * Clean, easy to understand code
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccessLevel } from '../hooks/useAccessLevel'
import { SubscriptionOnboardingModal } from '../components/auth/SubscriptionOnboardingModal'
import { SUPPORT_EMAIL } from '../config/brandConfig'

interface Subscription {
  id: string
  status: string
  plan_type: string
  amount: number
  current_period_end: string
  cancel_at_period_end: boolean
  trial_end: string | null
  // One-time year access rows (the $50 model) carry these and have no plan
  stripe_price_id?: string | null
  stripe_subscription_id?: string | null
  metadata?: { type?: string; cancellation?: { requested_at?: string; reverted_at?: string } } | null
  plan: {
    name: string
    monthly_price: number
    annual_price: number
    stripe_price_id_monthly: string
    stripe_price_id_annual: string
  } | null
}

// The $50 one-time year access: a single payment, no Stripe subscription,
// nothing renews. Such rows have a sentinel "onetime_" id and no plan.
const isOneTimeAccess = (s: Subscription | null): boolean =>
  !!s && (
    String(s.stripe_subscription_id || '').startsWith('onetime_') ||
    s.stripe_price_id === 'onetime_year_access' ||
    s.metadata?.type === 'one_time_year' ||
    !s.plan
  )

// Helper function to display status with custom labels
const getStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    'trialing': 'Intro Period',
    'active': 'Active',
    'canceled': 'Cancelled',
    'past_due': 'Past Due',
    'incomplete': 'Processing'
  }
  return statusMap[status] || status
}

// Helper function to format dates consistently and avoid timezone issues
const formatDate = (isoString: string): string => {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC' // Prevent timezone conversion issues
  })
}

// Helper function to get plan display name
const getPlanDisplayName = (planName: string): string => {
  // Map database plan names to display names
  if (planName.toLowerCase().includes('standard')) {
    return 'Full Private Access'
  }
  return planName
}

export function SubscriptionPage() {
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showSubscribeModal, setShowSubscribeModal] = useState(false)

  // Get access level to handle trial users (deferred or given_premium from invite)
  const { accessLevel, refresh: refreshAccessLevel } = useAccessLevel()
  const isDeferredTrial = accessLevel?.state === 'DEFERRED_TRIAL'
  const isDeferredTrialExpired = accessLevel?.state === 'DEFERRED_TRIAL_EXPIRED'
  const isGivenPremiumExpired = accessLevel?.state === 'GIVEN_PREMIUM_EXPIRED'
  const isGivenPremiumFromInvite = accessLevel?.state === 'GIVEN_PREMIUM' && accessLevel?.source === 'invite_premium'

  // NEW: Handle grace period states (webhook failed, user still has access but needs to subscribe)
  const isTrialExpiredGrace = accessLevel?.state === 'TRIAL_EXPIRED_GRACE'
  const isSubscriptionExpiredGrace = accessLevel?.state === 'SUBSCRIPTION_EXPIRED_GRACE'
  const isTrialExpired = accessLevel?.state === 'TRIAL_EXPIRED'
  const isSubscriptionExpired = accessLevel?.state === 'SUBSCRIPTION_PERIOD_EXPIRED'

  // Any expired trial state (no grace period left)
  const isAnyTrialExpired = isDeferredTrialExpired || isGivenPremiumExpired || isTrialExpired || isSubscriptionExpired
  // Any grace period state (has access but needs to subscribe urgently)
  const isInGracePeriod = isTrialExpiredGrace || isSubscriptionExpiredGrace
  // Any active trial state (no subscription)
  const isAnyActiveTrial = isDeferredTrial || isGivenPremiumFromInvite

  useEffect(() => {
    fetchSubscription()
  }, [])

  const fetchSubscription = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token') // Fixed: was 'access_token', should be 'auth_token'
      const response = await fetch(`${API_URL}/api/subscriptions/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setSubscription(data.subscription)
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    try {
      setActionLoading(true)

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ immediate: false })
      })

      if (!response.ok) {
        throw new Error('Failed to cancel subscription')
      }

      setShowCancelModal(false)
      await fetchSubscription()

      const updatedSub = await response.json()
      const isCurrentlyTrialing = updatedSub.subscription?.status === 'trialing'
      const endDate = isCurrentlyTrialing && updatedSub.subscription?.trial_end
        ? formatDate(updatedSub.subscription.trial_end)
        : formatDate(updatedSub.subscription?.current_period_end || subscription?.current_period_end || '')

      const message = isOneTimeAccess(subscription)
        ? `Cancellation recorded. Your access continues until ${endDate} and you won't be charged again.`
        : isCurrentlyTrialing
        ? `Subscription canceled. You'll keep access until your trial ends on ${endDate}.`
        : `Subscription canceled. You'll keep access until ${endDate}.`

      alert(message)
    } catch (error: any) {
      alert(error.message || 'Failed to cancel subscription')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    try {
      setActionLoading(true)

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/api/subscriptions/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to reactivate subscription')
      }

      await fetchSubscription()
      alert(isOneTimeAccess(subscription) ? 'Done - your access continues as before.' : 'Subscription reactivated!')
    } catch (error: any) {
      alert(error.message || 'Failed to reactivate subscription')
    } finally {
      setActionLoading(false)
    }
  }

  const handleChangePlan = async (newPlanType: 'monthly' | 'annual') => {
    if (!subscription) return

    const newPriceId = newPlanType === 'monthly'
      ? subscription.plan?.stripe_price_id_monthly
      : subscription.plan?.stripe_price_id_annual

    try {
      setActionLoading(true)

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/api/subscriptions/change-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ priceId: newPriceId })
      })

      if (!response.ok) {
        throw new Error('Failed to change plan')
      }

      await fetchSubscription()
      alert(`Plan change scheduled! You'll switch to ${newPlanType} on your next billing date.`)
    } catch (error: any) {
      alert(error.message || 'Failed to change plan')
    } finally {
      setActionLoading(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-braydin-olive"></div>
      </div>
    )
  }

  // PRIORITY 1: Users in grace period - show urgent subscribe UI
  // These users may or may not have a subscription record, but they need to subscribe NOW
  if (isInGracePeriod) {
    const graceDaysLeft = accessLevel?.graceDaysRemaining || 0
    const graceEndDate = accessLevel?.expiresAt ? formatDate(accessLevel.expiresAt) : 'N/A'
    const userId = localStorage.getItem('user_id') || ''

    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/account')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="mr-2">←</span>
            <span className="text-sm font-medium">Back to Account</span>
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Subscription</h1>

          {/* Grace Period Urgent Banner */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6 mb-6">
            <div className="text-center">
              <span className="text-5xl mb-4 block">🚨</span>
              <h3 className="text-2xl font-bold text-red-900 mb-2">
                {isSubscriptionExpiredGrace ? 'Your Subscription Has Expired!' : 'Your Trial Has Ended!'}
              </h3>
              <p className="text-red-700 mb-2 text-lg font-semibold">
                You have {graceDaysLeft} day{graceDaysLeft !== 1 ? 's' : ''} left to subscribe before losing access.
              </p>
              <p className="text-red-600 mb-6">
                Access expires: {graceEndDate}
              </p>
              <button
                onClick={() => setShowSubscribeModal(true)}
                className="px-10 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold text-xl transition-colors shadow-lg"
              >
                Subscribe Now to Keep Access
              </button>
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="font-bold text-braydin-ink mb-3">What happens next?</h3>
            <ul className="space-y-2 text-braydin-oliveDark text-sm">
              <li className="flex items-start gap-2">
                <span className="text-braydin-olive">✓</span>
                <span>Add a payment method to start your subscription</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-braydin-olive">✓</span>
                <span>Your access will continue uninterrupted</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-braydin-olive">✓</span>
                <span>Cancel anytime from this page</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Subscribe Modal */}
        {showSubscribeModal && (
          <SubscriptionOnboardingModal
            isOpen={showSubscribeModal}
            userId={userId}
            inviteToken={null}
            onComplete={() => {
              setShowSubscribeModal(false)
              refreshAccessLevel()
              fetchSubscription()
            }}
            onClose={() => setShowSubscribeModal(false)}
            allowSkip={false}
          />
        )}

      </div>
    )
  }

  // PRIORITY 2: Trial users without subscription (deferred trial OR given_premium from invite)
  // Note: Grace period users are handled above, this is for users without a subscription record
  if (!subscription && (isAnyActiveTrial || isAnyTrialExpired)) {
    const trialDaysLeft = accessLevel?.trialDaysRemaining || 0
    const trialEndDate = accessLevel?.expiresAt ? formatDate(accessLevel.expiresAt) : 'N/A'
    const userId = localStorage.getItem('user_id') || ''

    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/account')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="mr-2">←</span>
            <span className="text-sm font-medium">Back to Account</span>
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Subscription</h1>

          {/* Trial Status Banner */}
          {isAnyTrialExpired ? (
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-6 mb-6">
              <div className="text-center">
                <span className="text-4xl mb-4 block">⚠️</span>
                <h3 className="text-xl font-bold text-red-900 mb-2">Your Free Trial Has Ended</h3>
                <p className="text-red-700 mb-4">
                  Add a payment method to restore your access and continue enjoying premium content.
                </p>
                <button
                  onClick={() => setShowSubscribeModal(true)}
                  className="px-8 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold text-lg transition-colors"
                >
                  Subscribe Now
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <span className="text-3xl">⏰</span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-yellow-900 mb-1">
                    Free Trial - {trialDaysLeft} days left
                  </h3>
                  <p className="text-yellow-700 mb-4">
                    Your trial ends on {trialEndDate}. Add a payment method to ensure uninterrupted access.
                  </p>
                  <button
                    onClick={() => setShowSubscribeModal(true)}
                    className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-bold transition-colors"
                  >
                    Add Payment Method & Start Subscription
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Trial Info Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Free Trial Access</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                <span className={`font-medium ${isAnyTrialExpired ? 'text-red-600' : 'text-yellow-600'}`}>
                  {isAnyTrialExpired ? 'Expired' : 'Active'}
                </span>
              </div>
              {!isAnyTrialExpired && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Days Remaining</span>
                    <span className="font-medium">{trialDaysLeft}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Trial Ends</span>
                    <span className="font-medium">{trialEndDate}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* What happens next card */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="font-bold text-braydin-ink mb-3">How it works</h3>
            <ul className="space-y-2 text-braydin-oliveDark text-sm">
              <li className="flex items-start gap-2">
                <span className="text-braydin-olive">✓</span>
                <span>Add a payment method to lock in your subscription</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-braydin-olive">✓</span>
                <span>You won't be charged until your trial ends</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-braydin-olive">✓</span>
                <span>Your subscription will automatically continue after trial</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-braydin-olive">✓</span>
                <span>Cancel anytime before trial ends to avoid charges</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Subscribe Modal */}
        {showSubscribeModal && (
          <SubscriptionOnboardingModal
            isOpen={showSubscribeModal}
            userId={userId}
            inviteToken={null}
            onComplete={() => {
              setShowSubscribeModal(false)
              refreshAccessLevel()
              fetchSubscription()
            }}
            onClose={() => setShowSubscribeModal(false)}
            allowSkip={false}
          />
        )}

      </div>
    )
  }

  // No subscription state (not on trial either)
  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No Active Subscription</h1>
          <p className="text-gray-600 mb-6">You don't have an active subscription.</p>
          <button
            onClick={() => setShowSubscribeModal(true)}
            className="px-6 py-3 bg-braydin-olive text-white rounded-lg hover:bg-braydin-oliveDark font-medium transition-colors"
          >
            Subscribe Now
          </button>
        </div>

        {/* Subscribe Modal */}
        {showSubscribeModal && (
          <SubscriptionOnboardingModal
            isOpen={showSubscribeModal}
            userId={localStorage.getItem('user_id') || ''}
            inviteToken={null}
            onComplete={() => {
              setShowSubscribeModal(false)
              refreshAccessLevel()
              fetchSubscription()
            }}
            onClose={() => setShowSubscribeModal(false)}
            allowSkip={false}
          />
        )}
      </div>
    )
  }

  const isTrialing = subscription.status === 'trialing'
  const isCanceled = subscription.cancel_at_period_end
  const isPastDue = subscription.status === 'past_due'
  const isOneTime = isOneTimeAccess(subscription)

  // Check if trial has 0 days left (expired)
  const trialDaysLeft = subscription.trial_end ? Math.max(0, Math.ceil((new Date(subscription.trial_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/account')}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <span className="mr-2">←</span>
          <span className="text-sm font-medium">Back to Account</span>
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Subscription</h1>

        {/* Status Banners */}
        {isTrialing && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⏰</span>
              <div className="flex-1">
                <p className="font-semibold text-yellow-800">
                  Free Trial - {subscription.trial_end ? Math.max(0, Math.ceil((new Date(subscription.trial_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0} days left
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Add a payment method below to keep your access after your trial ends.
                </p>
              </div>
            </div>
          </div>
        )}

        {isPastDue && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="font-semibold text-red-900">Payment Failed</p>
            <p className="text-sm text-red-700 mt-1">
              Update your payment method to restore access.
            </p>
            <button
              onClick={() => navigate('/payment-methods')}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            >
              Update Payment Method
            </button>
          </div>
        )}

        {isCanceled && isOneTime && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <p className="font-semibold text-gray-900">Cancellation recorded</p>
            <p className="text-sm text-gray-600 mt-1">
              Your access continues until {formatDate(subscription.current_period_end)} and nothing will be charged. Changed your mind?
            </p>
            <button
              onClick={handleReactivate}
              disabled={actionLoading}
              className="mt-3 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
            >
              Keep my access
            </button>
          </div>
        )}

        {isCanceled && !isOneTime && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="font-semibold text-yellow-900">Subscription Ending</p>
            <p className="text-sm text-yellow-700 mt-1">
              Your subscription will end on {
                isTrialing && subscription.trial_end
                  ? formatDate(subscription.trial_end)
                  : formatDate(subscription.current_period_end)
              }.
              You'll lose access after this date.
            </p>
            <button
              onClick={handleReactivate}
              disabled={actionLoading}
              className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium disabled:opacity-50"
            >
              Reactivate Subscription
            </button>
          </div>
        )}

        {/* Expired Trial - Show Subscribe Button */}
        {isTrialing && trialDaysLeft <= 0 && (
          <div className="bg-[#f6f3ee] border-2 border-[#6b7850] rounded-lg p-6 mb-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-[#21231c] mb-2">Your Free Trial Has Ended</h3>
              <p className="text-[#5c5b44] mb-4">
                Your subscription will begin billing. Make sure you have a valid payment method.
              </p>
              <button
                onClick={() => navigate('/payment-methods')}
                className="px-8 py-4 bg-[#5c5b44] text-white rounded-lg hover:bg-[#5c5b44] font-bold text-lg transition-colors"
              >
                Manage Payment Method
              </button>
            </div>
          </div>
        )}

        {/* Current Plan Card */}
        {isOneTime ? (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">1 Year of Access</h2>
                <p className="text-gray-600">One-time payment</p>
              </div>
            </div>
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                <span className="font-medium">{isCanceled ? 'Cancellation recorded' : 'Active'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Access until</span>
                <span className="font-medium">{formatDate(subscription.current_period_end)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Renews automatically</span>
                <span className="font-medium">No</span>
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500">This was a single payment for one year of access. You will not be charged again.</p>
          </div>
        ) : (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{getPlanDisplayName(subscription.plan?.name || '')}</h2>
              <p className="text-gray-600">{subscription.plan_type === 'monthly' ? 'Monthly' : 'Annual'} billing</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900">
                ${subscription.plan_type === 'monthly' ? subscription.plan?.monthly_price : subscription.plan?.annual_price}
              </div>
              <div className="text-sm text-gray-600">
                /{subscription.plan_type === 'monthly' ? 'month' : 'year'}
              </div>
            </div>
          </div>

          <div className="border-t pt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status</span>
              <span className="font-medium">{getStatusDisplay(subscription.status)}</span>
            </div>
            {isTrialing && subscription.trial_end && (
              <div className="flex justify-between">
                <span className="text-gray-600">Trial ends</span>
                <span className="font-medium">{formatDate(subscription.trial_end)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Next billing date</span>
              <span className="font-medium">{
                isTrialing && subscription.trial_end
                  ? formatDate(subscription.trial_end)
                  : formatDate(subscription.current_period_end)
              }</span>
            </div>
          </div>
        </div>
        )}

        {/* Payment Method Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-4">Payment Method</h3>
          <button
            onClick={() => navigate('/payment-methods')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
          >
            Manage Payment Methods
          </button>
        </div>

        {/* Plan Change Card */}
        {!isCanceled && !isOneTime && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="font-bold text-gray-900 mb-2">Change Plan</h3>
            <p className="text-sm text-gray-600 mb-4">Changes take effect on your next billing date</p>

            {subscription.plan_type === 'monthly' ? (
              <button
                onClick={() => handleChangePlan('annual')}
                disabled={actionLoading}
                className="px-6 py-3 bg-braydin-olive text-white rounded-lg hover:bg-braydin-oliveDark font-medium transition-colors disabled:opacity-50"
              >
                Switch to Yearly (${subscription.plan?.annual_price}/year) - Save ${((subscription.plan?.monthly_price ?? 0) * 12) - (subscription.plan?.annual_price ?? 0)}
              </button>
            ) : (
              <button
                onClick={() => handleChangePlan('monthly')}
                disabled={actionLoading}
                className="px-6 py-3 bg-braydin-olive text-white rounded-lg hover:bg-braydin-oliveDark font-medium transition-colors disabled:opacity-50"
              >
                Switch to Monthly (${subscription.plan?.monthly_price}/month)
              </button>
            )}
          </div>
        )}

        {/* Support - a real address and a mailto button, above the cancel line */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-1">Support</h3>
          <p className="text-sm text-gray-600 mb-4">
            Questions about your access or a payment? Email us and a real person will reply.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-700 break-all">
              Support: <span className="font-medium">{SUPPORT_EMAIL}</span>
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Question about my access')}`}
              className="inline-flex items-center justify-center px-5 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition-colors"
            >
              Email support
            </a>
          </div>
        </div>

        {/* Cancel - one-time access: a quiet line, only here on Manage Subscription */}
        {!isCanceled && isOneTime && (
          <p className="mt-2 text-center text-xs text-gray-400">
            Thinking of leaving?{' '}
            <button
              type="button"
              onClick={() => setShowCancelModal(true)}
              className="underline hover:text-gray-600"
            >
              Cancel your access
            </button>
          </p>
        )}

        {/* Cancel Card */}
        {!isCanceled && !isOneTime && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold text-gray-900 mb-2">Cancel Subscription</h3>
            <p className="text-sm text-gray-600 mb-4">
              You'll keep access until {
                isTrialing && subscription.trial_end
                  ? formatDate(subscription.trial_end)
                  : formatDate(subscription.current_period_end)
              }
            </p>
            <button
              onClick={() => setShowCancelModal(true)}
              className="px-4 py-2 border border-gray-400 text-gray-500 rounded-lg hover:bg-gray-50 text-sm transition-colors"
            >
              Thinking of leaving?
            </button>
          </div>
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-900 mb-4">{isOneTime ? 'Cancel your access?' : 'Cancel Subscription?'}</h3>
              <p className="text-gray-600 mb-6">
                {isOneTime ? (
                  <>Your payment was a one-time purchase, not a subscription. Nothing renews and you will not be charged again. If you cancel, we'll record it and your access will end on {formatDate(subscription.current_period_end)}. You can change your mind any time before then.</>
                ) : isTrialing && subscription.trial_end ? (
                  <>You'll keep access until your trial ends on {formatDate(subscription.trial_end)}, then your access will end. You can reactivate anytime before then.</>
                ) : (
                  <>You'll keep access until {formatDate(subscription.current_period_end)}, then your access will end. You can reactivate anytime before then.</>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  {isOneTime ? 'Keep my access' : 'Keep Subscription'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {actionLoading ? 'Canceling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}
